import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { resourceState, shouldRetryMarketing, toMarketingError, type ResourceState } from '@/features/marketing-shared';
import { emailMarketingKeys, fetchEmailCampaign } from '../api/email-marketing-api';
import { useEmailMarketing } from './context';
import type { EmailCampaign } from './types';

export const RECIPIENTS_PAGE_SIZE = 50;

/**
 * Detalhe de uma campanha (métricas, evolução, erros e uma página de
 * destinatários). Enquanto a API responde, a linha da lista já desenha o
 * cabeçalho; os blocos que só o detalhe tem aparecem depois.
 */
export function useEmailCampaignDetail(
  id: string | undefined,
  page: number,
): { campaign: EmailCampaign | null; state: ResourceState; detailPending: boolean } {
  const { idPassbook, campaigns, campaignsState, scenario } = useEmailMarketing();
  const query = useQuery({
    queryKey: emailMarketingKeys.campaign(idPassbook ?? 'platform', id ?? '-', page),
    queryFn: () => fetchEmailCampaign(id!, idPassbook!, { page, pageSize: RECIPIENTS_PAGE_SIZE }),
    enabled: Boolean(id && idPassbook),
    retry: shouldRetryMarketing,
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });

  const fromList = campaigns.find((item) => item.id === id) ?? null;
  if (scenario === 'carregando') return { campaign: null, state: { ...campaignsState, phase: 'loading' }, detailPending: true };
  if (scenario === 'erro') return { campaign: null, state: campaignsState, detailPending: false };
  if (scenario === 'vazio') return { campaign: null, state: { ...campaignsState, phase: 'ready' }, detailPending: false };

  const detail = resourceState(query, toMarketingError);
  if (query.data) return { campaign: query.data, state: detail, detailPending: query.isPlaceholderData };
  if (detail.phase === 'error' && detail.error?.kind !== 'not_found' && fromList) {
    // A lista respondeu e o detalhe não: mostra o que já se sabe, sem esconder a falha.
    return { campaign: fromList, state: detail, detailPending: false };
  }
  if (detail.phase === 'loading' && fromList) return { campaign: fromList, state: { ...detail, phase: 'ready' }, detailPending: true };
  if (detail.phase === 'loading' && campaignsState.phase === 'loading') return { campaign: null, state: detail, detailPending: true };
  return { campaign: null, state: detail, detailPending: false };
}
